import axios from "axios";
import { useState, useContext, useEffect, useRef } from "react";
import { AuthContext } from "../../context/AuthContext";
import { NavigationContext } from "../../context/NavigationContext";
import { Table, Typography, message, Spin, Modal, Input, Button, DatePicker, Space, Timeline, Tag } from "antd";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import dayjs from "dayjs";

const { Title, Text } = Typography;
const { TextArea } = Input;
const { RangePicker } = DatePicker;

export default function Otapplication() {
    const { authData, backUrl } = useContext(AuthContext);
    const { navigationParams, clearNavigationParams } = useContext(NavigationContext);
    const [data, setData] = useState([]);
    const [filteredData, setFilteredData] = useState([]);
    const [loading, setLoading] = useState(false);
    const [isModalVisible, setIsModalVisible] = useState(false);
    const [selectedRecord, setSelectedRecord] = useState(null);
    const [reason, setReason] = useState("");
    const [submitting, setSubmitting] = useState(false);
    const [dateRange, setDateRange] = useState(null);
    const [pagination, setPagination] = useState({
        current: 1,
        pageSize: 10,
        total: 0,
    });

    // ✅ Notification highlight state
    const [targetOtId, setTargetOtId] = useState(null);
    const highlightRowRef = useRef(null);

    const statusColors = {
        PENDING: "#faad14",
        APPROVED: "green",
        REJECTED: "red",
    };

    const getStatusColor = (status) => {
        if (!status) return "#faad14";
        const upperStatus = status.toUpperCase();
        return statusColors[upperStatus] || "#faad14";
    };

    // Format date as dd/mm/yyyy
    const formatDate = (dateString) => {
        if (!dateString) return "-";
        const date = new Date(dateString);
        const day = String(date.getDate()).padStart(2, "0");
        const month = String(date.getMonth() + 1).padStart(2, "0");
        const year = date.getFullYear();
        return `${day}/${month}/${year}`;
    };

    // Format datetime as dd/mm/yyyy HH:mm:ss
    const formatDateTime = (dateString) => {
        if (!dateString) return "-";
        const date = new Date(dateString);
        const day = String(date.getDate()).padStart(2, "0");
        const month = String(date.getMonth() + 1).padStart(2, "0");
        const year = date.getFullYear();
        const hours = String(date.getHours()).padStart(2, "0");
        const minutes = String(date.getMinutes()).padStart(2, "0");
        const seconds = String(date.getSeconds()).padStart(2, "0");
        return `${day}/${month}/${year} ${hours}:${minutes}:${seconds}`;
    };

    const fetchData = async () => {
        setLoading(true);
        try {
            const params = {};

            // 🔥 Add date filters if selected
            if (dateRange && dateRange.length === 2) {
                params.from_date = dayjs(dateRange[0]).format("YYYY-MM-DD");
                params.to_date = dayjs(dateRange[1]).format("YYYY-MM-DD");
            }

            const res = await axios.get(
                `${backUrl}/api/payroll/ot-eligible-days/`,
                {
                    headers: {
                        Authorization: `Bearer ${authData?.access}`,
                    },
                    params, // ✅ backend query params
                }
            );

            const formatted = (res.data.results || res.data).map((item) => ({
                ...item,
                approval_logs: item.approval_logs || [],
            }));

            setData(formatted);
            setFilteredData(formatted);
            setPagination((prev) => ({
                ...prev,
                total: formatted.length,
            }));
        } catch (err) {
            message.error("Failed to fetch OT data");
            console.error("Error fetching OT eligible days:", err);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        if (authData?.access) {
            fetchData();
        }
    }, [authData, dateRange]);

    // ✅ Consume navigation params from notification click
    useEffect(() => {
        if (navigationParams?.otId) {
            console.log("🎯 Otapplication: Received navigation params:", navigationParams);
            setTargetOtId(navigationParams.otId);
            clearNavigationParams();
        }
    }, [navigationParams, clearNavigationParams]);

    // ✅ Scroll to highlighted row after filteredData is populated
    useEffect(() => {
        if (targetOtId && filteredData.length > 0) {
            const targetIdNum = parseInt(targetOtId, 10);
            const found = filteredData.find((r) => r.id === targetIdNum);
            if (found) {
                console.log("🎯 Otapplication: Found target OT record:", found);
                setTimeout(() => {
                    if (highlightRowRef.current) {
                        highlightRowRef.current.scrollIntoView({
                            behavior: "smooth",
                            block: "center",
                        });
                    }
                }, 300);
            }
        }
    }, [targetOtId, filteredData]);

    // Filter data by date range
    useEffect(() => {
        if (!dateRange) {
            setFilteredData(data);
            setPagination((prev) => ({
                ...prev,
                current: 1,
                total: data.length,
            }));
            return;
        }

        const [startDate, endDate] = dateRange;
        const filtered = data.filter((item) => {
            const itemDate = dayjs(item.date, "YYYY-MM-DD");
            return itemDate.isSameOrAfter(startDate, "day") && itemDate.isSameOrBefore(endDate, "day");
        });

        setFilteredData(filtered);
        setPagination((prev) => ({
            ...prev,
            current: 1,
            total: filtered.length,
        }));
    }, [dateRange, data]);

    const handleDateRangeChange = (dates) => {
        setDateRange(dates);
    };

    const handleClearFilter = () => {
        setDateRange(null);
    };

    const handleRequestOT = (record) => {
        setSelectedRecord(record);
        setIsModalVisible(true);
    };

    const handleSubmit = async () => {
        setSubmitting(true);
        if (!reason.trim()) {
            message.warning("Please provide a reason before submitting");
            setSubmitting(false);
            return;
        }

        const payload = {
            attendance_record_id: selectedRecord.id,
            employee_remarks: reason,
            requested_ot_hours: selectedRecord.potential_ot_hours,
        };

        try {
            const res = await axios.post(
                `${backUrl}/api/payroll/ot-requests/`,
                payload,
                {
                    headers: {
                        Authorization: `Bearer ${authData?.access}`,
                    },
                }
            );
            toast.success("OT request submitted successfully!");
            setIsModalVisible(false);
            setReason("");
            fetchData();
        } catch (err) {
            console.error("Error submitting OT request:", err);

            const errorMessage =
                err.response?.data?.data ||
                err.response?.data?.message ||
                err.response?.data?.error ||
                err.response?.data ||
                "Failed to submit OT request";

            toast.error(errorMessage);
        } finally {
            setSubmitting(false);
        }
    };

    const handleTableChange = (newPagination) => {
        setPagination(newPagination);
    };

    const approvalColumns = [
        { title: "Order", dataIndex: "level_order", key: "level_order", align: "center" },
        { title: "Approver", dataIndex: "approver_name", key: "approver_name", align: "center" },
        {
            title: "Status",
            dataIndex: "status",
            key: "status",
            align: "center",
            render: (text) => (
                <span style={{ fontWeight: 500, color: getStatusColor(text) }}>
                    {text ? text.charAt(0).toUpperCase() + text.slice(1).toLowerCase() : "-"}
                </span>
            ),
        },
        {
            title: "Remarks",
            key: "remarks",
            align: "center",
            render: (_, record) => (
                <div style={{ fontSize: "12px" }}>
                    {record.remarks && (
                        <div style={{ marginBottom: "4px" }}>
                            <Text strong style={{ fontSize: "11px" }}>Remark: </Text>
                            {record.remarks}
                        </div>
                    )}
                    {record.cancellation_reason && (
                        <div style={{ borderTop: record.remarks ? "1px dashed #d9d9d9" : "none", paddingTop: record.remarks ? "4px" : "0" }}>
                            <Text strong style={{ fontSize: "11px", color: "red" }}>Cancellation Remark: </Text>
                            {record.cancellation_reason}
                        </div>
                    )}
                    {!record.remarks && !record.cancellation_reason && "-"}
                </div>
            )
        },
        {
            title: "Timestamp",
            key: "timestamp",
            align: "center",
            render: (_, record) => (
                <div style={{ fontSize: "11px", color: "#595959" }}>
                    {record.updated_at && (
                        <div style={{ marginBottom: "4px" }}>
                            <Text strong style={{ fontSize: "10px" }}>On: </Text>
                            {formatDateTime(record.updated_at)}
                        </div>
                    )}
                    {(record.cancellation_approved_on || record.cancellation_rejected_on) && (
                        <div style={{ borderTop: record.updated_at ? "1px dashed #d9d9d9" : "none", paddingTop: record.updated_at ? "4px" : "0" }}>
                            <Text strong style={{ fontSize: "10px" }}>Cancellation On: </Text>
                            {formatDateTime(record.cancellation_approved_on || record.cancellation_rejected_on)}
                        </div>
                    )}
                    {!record.updated_at && !record.cancellation_approved_on && !record.cancellation_rejected_on && "-"}
                </div>
            )
        },
    ];

    const columns = [
        {
            title: "Date",
            dataIndex: "date",
            key: "date",
            align: "center",
            render: (text) => formatDate(text),
        },
        {
            title: "First Check-In",
            dataIndex: "first_check_in",
            key: "first_check_in",
            align: "center",
            render: (text) => formatDateTime(text),
        },
        {
            title: "Last Check-Out",
            dataIndex: "last_check_out",
            key: "last_check_out",
            align: "center",
            render: (text) => formatDateTime(text),
        },
        { title: "Total Time", dataIndex: "total_time", key: "total_time", align: "center" },
        { title: "Potential OT Hours", dataIndex: "potential_ot_hours", key: "potential_ot_hours", align: "center" },
        {
            title: "Status",
            key: "status",
            align: "center",
            render: (_, record) => {
                let displayStatus = "PENDING";
                const logs = record.approval_logs || [];

                if (logs.length > 0) {
                    const hasRejected = logs.some((log) => log.status === "REJECTED");
                    const allApproved = logs.every((log) => log.status === "APPROVED");

                    if (hasRejected) {
                        displayStatus = "REJECTED";
                    } else if (allApproved) {
                        displayStatus = "APPROVED";
                    } else {
                        displayStatus = "PENDING";
                    }
                } else if (record.status) {
                    displayStatus = record.status;
                }

                const safeStatus = String(displayStatus || "PENDING");

                return (
                    <span style={{ fontWeight: 500, color: getStatusColor(safeStatus) }}>
                        {safeStatus.charAt(0).toUpperCase() + safeStatus.slice(1).toLowerCase()}
                    </span>
                );
            },
        },
        {
            title: "Approval Hierarchy",
            key: "approval_logs",
            width: 400,
            render: (_, record) => {
                const logs = record.approval_logs || [];
                if (!logs.length) return "-";

                return (
                    <Timeline style={{ marginTop: "20px" }}>
                        {logs.map((log, index) => {
                            const statusColor =
                                log.status === "APPROVED"
                                    ? "green"
                                    : log.status === "REJECTED"
                                        ? "red"
                                        : "gray";

                            return (
                                <Timeline.Item key={index} color={statusColor}>
                                    <div style={{ display: "flex", flexDirection: "column" }}>
                                        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start" }}>
                                            <div>
                                                <Text strong style={{ fontSize: "14px" }}>
                                                    {log.approver_name}
                                                </Text>
                                            </div>
                                            <Tag color={statusColor}>{log.status}</Tag>
                                        </div>

                                        {/* Action Timestamps */}
                                        <div style={{ marginTop: "4px", fontSize: "12px", color: "#8c8c8c" }}>
                                            {log.updated_at && (
                                                <div>On: {dayjs(log.updated_at).format("DD MMM YYYY [at] hh:mm A")}</div>
                                            )}
                                        </div>

                                        {/* Action Remarks */}
                                        <div style={{ marginTop: "6px", fontSize: "13px" }}>
                                            {log.remarks && (
                                                <div style={{ marginBottom: "2px" }}>
                                                    <Text strong>Remark: </Text>
                                                    <Text>{log.remarks}</Text>
                                                </div>
                                            )}
                                        </div>
                                    </div>
                                </Timeline.Item>
                            );
                        })}
                    </Timeline>
                );
            },
        },
        {
            title: "Action",
            key: "action",
            align: "center",
            render: (_, record) =>
                record.ot_requested ? (
                    <p>OT Request Already Sent</p>
                ) : (
                    <Button
                        type="primary"
                        style={{ backgroundColor: "#0e497b" }}
                        onClick={() => handleRequestOT(record)}
                    >
                        Request OT
                    </Button>
                ),
        },
    ];

    return (
        <div style={{ margin: "30px" }}>
            <Title level={4} style={{ textAlign: "center", marginBottom: 20 }}>
                OT Eligible Days
            </Title>

            <Space style={{ marginBottom: 16 }}>
                <RangePicker
                    format="DD/MM/YYYY"
                    value={dateRange}
                    onChange={handleDateRangeChange}
                    placeholder={["Start Date", "End Date"]}
                />
                <Button onClick={handleClearFilter}>Clear Filter</Button>
            </Space>

            {loading ? (
                <div style={{ textAlign: "center", padding: "50px" }}>
                    <Spin size="large" />
                </div>
            ) : (
                <Table
                    columns={columns}
                    dataSource={filteredData}
                    rowKey="id"
                    bordered
                    pagination={{
                        current: pagination.current,
                        pageSize: pagination.pageSize,
                        total: pagination.total,
                        showSizeChanger: true,
                        showTotal: (total) => `Total ${total} records`,
                        pageSizeOptions: ["10", "20", "50", "100"],
                    }}
                    onChange={handleTableChange}
                    rowClassName={(record) => {
                        const targetIdNum = parseInt(targetOtId, 10);
                        return record.id === targetIdNum ? "highlighted-row" : "";
                    }}
                    onRow={(record) => {
                        const targetIdNum = parseInt(targetOtId, 10);
                        return {
                            ref: record.id === targetIdNum ? highlightRowRef : null,
                        };
                    }}
                    components={{
                        header: {
                            cell: (props) => (
                                <th
                                    {...props}
                                    style={{
                                        backgroundColor: "#0e497b",
                                        color: "white",
                                    }}
                                >
                                    {props.children}
                                </th>
                            ),
                        },
                    }}
                />
            )}

            <Modal
                title="Request OT"
                open={isModalVisible}
                onCancel={() => setIsModalVisible(false)}
                footer={[
                    <Button key="cancel" onClick={() => setIsModalVisible(false)}>
                        Cancel
                    </Button>,
                    <Button
                        key="submit"
                        type="primary"
                        loading={submitting}
                        disabled={submitting}
                        style={{ backgroundColor: "#0e497b" }}
                        onClick={handleSubmit}
                    >
                        {submitting ? "Submitting..." : "Submit"}
                    </Button>,
                ]}
            >
                <p>
                    <strong>Date:</strong> {formatDate(selectedRecord?.date)}
                </p>
                <TextArea
                    rows={4}
                    placeholder="Enter reason for requesting OT"
                    value={reason}
                    onChange={(e) => setReason(e.target.value)}
                />
            </Modal>

            <ToastContainer position="top-right" autoClose={3000} />
        </div>
    );
}